import type { FactId, StrategyId } from "../model.ts";

/** Product scheduling defaults, not mastery rules or validated learning intervals. */
export const SCHEDULING_POLICY = {
  version: "scheduling-v1",
  maximumFactPresentations: 2,
  minimumInterveningExercises: 3,
  inactivityForNewDoseMs: 30 * 60_000,
  maximumStrategyActivities: 1,
  maximumNewIntroductions: 1,
  novelCoreEvery: 4,
} as const;

export interface FactDose {
  readonly presentations: number;
  readonly successes: number;
  readonly difficulties: number;
  readonly eligibleAfterOrdinal: number;
  readonly lastExposedAt: string;
}
export interface SchedulingState {
  readonly profileId: string;
  readonly revision: number;
  readonly policyVersion: string;
  readonly windowStartedAt: string;
  readonly lastActivityAt: string;
  readonly independentOrdinal: number;
  readonly facts: Readonly<Record<FactId, FactDose>>;
  readonly strategyCounts: Readonly<Record<StrategyId, number>>;
  readonly introductions: number;
  readonly repairStrategyIds: readonly StrategyId[];
  readonly repairFactIds: readonly FactId[];
  readonly recentFactIds: readonly FactId[];
  readonly coreSinceNovel: number;
  /** Lifetime scheduling coverage, separate from evidence and mastery. */
  readonly lifetimePresentations: Readonly<Record<FactId, number>>;
}

export function emptySchedulingState(profileId: string, now: string): SchedulingState {
  return { profileId, revision: 0, policyVersion: SCHEDULING_POLICY.version,
    windowStartedAt: now, lastActivityAt: now, independentOrdinal: 0, facts: {},
    strategyCounts: {}, introductions: 0, repairStrategyIds: [], repairFactIds: [],
    recentFactIds: [], coreSinceNovel: 0, lifetimePresentations: {} };
}

export function prepareSchedulingWindow(state: SchedulingState, now: string): SchedulingState {
  if (state.policyVersion !== SCHEDULING_POLICY.version) throw new Error("Onbekende schedulingversie.");
  if (!Number.isFinite(Date.parse(now))) throw new Error("Ongeldig schedulingtijdstip.");
  if (Date.parse(now) - Date.parse(state.lastActivityAt) < SCHEDULING_POLICY.inactivityForNewDoseMs) return state;
  return { ...state, windowStartedAt: now, facts: {}, strategyCounts: {},
    introductions: 0, recentFactIds: [] };
}

/** Help, wrong answers and abandoned presentations consume the same finite dose. */
export function factSchedulingBlock(state: SchedulingState, factId: FactId): string | null {
  const dose = state.facts[factId];
  if (state.repairFactIds.includes(factId)) return "REPAIR_REQUIRED";
  if (!dose) return null;
  if (dose.presentations >= SCHEDULING_POLICY.maximumFactPresentations) return "DOSE_COMPLETE";
  if (state.independentOrdinal < dose.eligibleAfterOrdinal) return "SPACING";
  return null;
}

function factDose(state: SchedulingState, factId: FactId, now: string): FactDose {
  return state.facts[factId] ?? { presentations: 0, successes: 0, difficulties: 0,
    eligibleAfterOrdinal: 0, lastExposedAt: now };
}

/** Reserve before display. An interrupted/reloaded item cannot refund its dose. */
export function reserveFact(state: SchedulingState, factId: FactId, now: string): SchedulingState {
  const block = factSchedulingBlock(state, factId);
  if (block) throw new Error(`Fact niet beschikbaar: ${block}.`);
  const previous = factDose(state, factId, now);
  return { ...state, lastActivityAt: later(state.lastActivityAt, now),
    facts: { ...state.facts, [factId]: { ...previous, presentations: previous.presentations + 1,
      eligibleAfterOrdinal: state.independentOrdinal + SCHEDULING_POLICY.minimumInterveningExercises,
      lastExposedAt: now } },
    lifetimePresentations: { ...state.lifetimePresentations, [factId]: (state.lifetimePresentations[factId] ?? 0) + 1 } };
}

/** Worked examples/help are exposures but never independent exercises or evidence. */
export function exposeFact(state: SchedulingState, factId: FactId, now: string): SchedulingState {
  const previous = factDose(state, factId, now);
  return { ...state, lastActivityAt: later(state.lastActivityAt, now),
    facts: { ...state.facts, [factId]: { ...previous, lastExposedAt: now,
      eligibleAfterOrdinal: state.independentOrdinal + SCHEDULING_POLICY.minimumInterveningExercises } } };
}

export function finishScheduledFact(
  state: SchedulingState, factId: FactId, correct: boolean | null, now: string,
  core: boolean, novel: boolean, strategyId: StrategyId | null = null,
): SchedulingState {
  const previous = factDose(state, factId, now);
  const difficulties = previous.difficulties + (correct === true ? 0 : 1);
  const independentOrdinal = state.independentOrdinal + 1;
  return { ...state, lastActivityAt: later(state.lastActivityAt, now), independentOrdinal,
    facts: { ...state.facts, [factId]: { ...previous, difficulties,
      successes: previous.successes + (correct === true ? 1 : 0), lastExposedAt: now,
      eligibleAfterOrdinal: independentOrdinal + SCHEDULING_POLICY.minimumInterveningExercises } },
    recentFactIds: [...state.recentFactIds, factId].slice(-12),
    coreSinceNovel: core ? (novel ? 0 : state.coreSinceNovel + 1) : state.coreSinceNovel,
    repairFactIds: difficulties >= 2 ? [...new Set([...state.repairFactIds, factId])] : state.repairFactIds,
    repairStrategyIds: difficulties >= 2 && strategyId
      ? [...new Set([...state.repairStrategyIds, strategyId])] : state.repairStrategyIds };
}

export function reserveStrategy(state: SchedulingState, id: StrategyId, introduction: boolean, now: string): SchedulingState {
  if ((state.strategyCounts[id] ?? 0) >= SCHEDULING_POLICY.maximumStrategyActivities) throw new Error("Strategiedosis is op.");
  if (introduction && state.introductions >= SCHEDULING_POLICY.maximumNewIntroductions) throw new Error("Introductiedosis is op.");
  return { ...state, lastActivityAt: later(state.lastActivityAt, now),
    strategyCounts: { ...state.strategyCounts, [id]: (state.strategyCounts[id] ?? 0) + 1 },
    introductions: state.introductions + (introduction ? 1 : 0) };
}

export function finishScheduledStrategy(state: SchedulingState, id: StrategyId, correct: boolean, repairedFactIds: readonly FactId[]): SchedulingState {
  return { ...state,
    repairStrategyIds: correct ? state.repairStrategyIds.filter((value) => value !== id)
      : [...new Set([...state.repairStrategyIds, id])],
    repairFactIds: correct ? state.repairFactIds.filter((value) => !repairedFactIds.includes(value)) : state.repairFactIds };
}

export function later(left: string, right: string): string {
  return Date.parse(left) > Date.parse(right) ? left : right;
}
